<!--
  @description Slide-out navigation drawer for the admin panel. Renders a hamburger icon that
  toggles a left-anchored SideMenu with links to all admin sections: packs, challenges, tags,
  feedback, admin management, and cross-links to the game and public website. Has no props —
  all routing is hardcoded to named routes.

  @example
  <AdminNav />

  @accessibility The hamburger icon is interactive but carries no ARIA label — consider adding
  aria-label="Open admin menu" to the icon element for screen-reader users.
-->
<template>
  <div class="admin-nav">
    <i class="admin-nav__menu-icon fi fi-sr-apps" @click="toggleMenu"></i>
    <SideMenu
      :isOpen="menuOpen"
      :isClosable="true"
      :leftMenu="true"
      @close="toggleMenu"
      class="admin-nav__slider"
    >
      <div class="admin-nav__settings-menu">
        <ul>
          <li>
            <router-link
              :to="{ name: 'packs' }"
              class="admin-nav__link"
              :class="{ active: $route.path.includes('/packs') }"
              >{{ $t('admin.nav.packs') }}</router-link
            >
          </li>
          <ul class="admin-nav__sublinks" v-show="$route.path.includes('/packs')">
            <li>
              <router-link :to="{ name: 'challenges' }" class="admin-nav__link">{{
                $t('admin.nav.challenges')
              }}</router-link>
            </li>
            <li>
              <router-link :to="{ name: 'tags' }" class="admin-nav__link">{{
                $t('admin.nav.tags')
              }}</router-link>
            </li>
          </ul>
          <li>
            <router-link :to="{ name: 'feedback' }" class="admin-nav__link">{{
              $t('admin.nav.feedback')
            }}</router-link>
          </li>
          <li>
            <router-link :to="{ name: 'add-admin' }" class="admin-nav__link">{{
              $t('admin.nav.add-admin')
            }}</router-link>
          </li>
          <li>
            <router-link :to="{ name: 'games' }" class="admin-nav__link">{{
              $t('admin.nav.go-to-game')
            }}</router-link>
          </li>
          <li>
            <router-link :to="{ name: 'home' }" class="admin-nav__link">{{
              $t('admin.nav.go-to-website')
            }}</router-link>
          </li>
        </ul>
      </div>
    </SideMenu>
  </div>
</template>

<script setup lang="ts">
import SideMenu from '@/components/common/Global/side-menu/SideMenu.vue'
import { ref } from 'vue'
const menuOpen = ref(false)

function toggleMenu() {
  menuOpen.value = !menuOpen.value
}
</script>

<style scoped lang="scss">
.admin-nav {
  &__menu-icon {
    position: fixed;
    top: 2rem;
    left: 2rem;
    font-size: 1.5rem;
    cursor: pointer;

    &:hover {
      color: var(--secondary-dark);
    }
  }

  &__slider {
    ul,
    .admin-nav__link {
      font-weight: 500;
      color: var(--text);
      list-style: none;
      text-decoration: none;
      cursor: pointer;
    }

    a.active {
      border-bottom: 1px solid var(--primary);
      color: var(--primary);
    }

    li {
      text-transform: uppercase;
      padding: 15px;
      margin-left: 16px;
      text-align: start;
    }

    .admin-nav__link {
      font-size: 0.9rem;
      padding-bottom: 4px;
      border-bottom: 1px solid transparent;

      &:hover {
        border-bottom: 1px solid var(--primary);
        color: var(--primary);
      }
    }

    .admin-nav__sublinks {
      transform: translateX(3rem);
      margin: 0 !important;
      border-left: 1px solid var(--text);

      li {
        padding: 0;

        a {
          font-size: 0.8rem;
        }
      }
    }

    .admin-nav__settings-menu {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: space-between;

      ul {
        padding: 0;
        margin-top: 50px;
        text-align: center;
      }
    }
  }
}
</style>
